import {
  Component,
  inject,
  OnInit,
  signal,
  HostListener,
  ElementRef,
  LOCALE_ID,
  input,
  effect,
  DestroyRef,
  ChangeDetectionStrategy,
} from '@angular/core';
import {
  AbstractControl,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  ValidationErrors,
  Validators,
} from '@angular/forms';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { ProductService } from '../../../services/product/product.service';
import { SuppliersService } from '../../../services/suppliers/suppliers.service';
import { AdminSuppliersResponseBody } from '../../../pages/admin-dashboard/admin-supplier/admin-supplier.model';
import { StockService } from '../../../services/product/stock/stock';
import {
  ProductAutocompleteItem,
  StockIncomingItem,
  SupplierItem,
} from '../../../pages/admin-dashboard/stock-management/add-all-stock/add-stock.model';
import { ProductPrices } from '../add-product-component/steps/product-info-step/product-prices/product-prices';
import { StockInformation } from './stock-information/stock-information';
import { StockTemporaryTable } from './stock-temporary-table/stock-temporary-table';
import { OneStockData } from '../../../pages/admin-dashboard/stock-management/edit-stock/edit-stock.model';
import { SuccessfulNotification } from '../../../utils/successful-notification/successful-notification';
import { Error } from '../../public-user-component/auth/shared/error/error';


@Component({
  selector: 'app-add-all-stock-form',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    CommonModule,
    ProductPrices,
    StockInformation,
    StockTemporaryTable,
    Error,
    SuccessfulNotification,
  ],
  templateUrl: './add-all-stock-form.html',
  styleUrl: './add-all-stock-form.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AddAllStockForm implements OnInit {
  private locale = inject(LOCALE_ID);
  private router = inject(Router);
  private stockService = inject(StockService);
  private productService = inject(ProductService);
  private suppliersService = inject(SuppliersService);
  private eRef = inject(ElementRef);
  private destroyRef = inject(DestroyRef);

  type = input<'create' | 'edit'>('create');
  stockId = input('');
  stockData = signal({});
  productId = input<number | null>(null);

  suppliers = signal<SupplierItem[]>([]);
  productsAutocomplete = signal<ProductAutocompleteItem[]>([]);
  temporaryStockList = signal<StockIncomingItem[]>([]);
  showProductAutocomplete = signal<boolean>(false);

  showSuccess = signal<boolean>(false);
  pageError = signal<string>('');

  integerValidator(control: AbstractControl): ValidationErrors | null {
    const value = control.value;
    if (value && !Number.isInteger(Number(value))) {
      return { notInteger: true };
    }
    return null;
  }
  stockForm = new FormGroup({
    productId: new FormControl<number | null>(null, Validators.required),
    productSearch: new FormControl('', Validators.required),
    supplierId: new FormControl<number | null>(null, Validators.required),
    quantity: new FormControl<number | null>(null, [Validators.required, Validators.min(1), this.integerValidator]),
    peoplePrice: new FormControl<number | null>(null, [
      Validators.required,
      Validators.min(0.1),
      Validators.max(1000000),
    ]),
    sellerDiscount: new FormControl<number | null>(null, [Validators.required, Validators.min(0), Validators.max(100)]),
    sellerPrice: new FormControl({ value: 0, disabled: true }),
    pharmacyDiscount: new FormControl<number | null>(null, [
      Validators.required,
      Validators.min(0),
      Validators.max(100),
    ]),
    pharmacyPrice: new FormControl({ value: 0, disabled: true }),
    batchNumber: new FormControl('', [
      Validators.required,
      Validators.maxLength(50),
      Validators.pattern('^[a-zA-Z0-9-]+$'),
    ]),
    mfgDate: new FormControl('', Validators.required),
    expDate: new FormControl('', Validators.required),
  });

  @HostListener('document:click', ['$event'])
  clickout(event: MouseEvent) {
    if (!this.eRef.nativeElement.contains(event.target)) {
      this.showProductAutocomplete.set(false);
    }
  }

  constructor() {
    effect(() => {
      const disableFields = ['batchNumber', 'peoplePrice', 'mfgDate', 'expDate'];
      const isEdit = this.type() === 'edit';
      disableFields.forEach((field) => {
        const ctrl = this.stockForm.get(field);
        isEdit ? ctrl?.disable() : ctrl?.enable();
      });

      const productIdCtrl = this.stockForm.get('productId');
      const productSearchCtrl = this.stockForm.get('productSearch');

      if (isEdit) {
        productIdCtrl?.clearValidators();
        productSearchCtrl?.clearValidators();
      } else {
        productIdCtrl?.setValidators(Validators.required);
        productSearchCtrl?.setValidators(Validators.required);
      }
      productIdCtrl?.updateValueAndValidity();
      productSearchCtrl?.updateValueAndValidity();
    });
  }

  ngOnInit() {
    this.loadSuppliers();
    if (this.type() === 'edit') {
      this.loadStock();
    }
    this.setupPriceCalculation();
  }

  loadStock() {
    this.stockService.getStockById(this.stockId()!).subscribe({
      next: (res: OneStockData) => {
        this.stockData.set(res);
        this.stockForm.patchValue({
          productId: +res.productId,
          batchNumber: res.attributes.filter((item) => item.attributeKey === 'BATCH_NUMBER')[0].attributeValue,
          expDate: res.attributes.filter((item) => item.attributeKey === 'EXP_DATE')[0].attributeValue,
          mfgDate: res.attributes.filter((item) => item.attributeKey === 'MFG_DATE')[0].attributeValue,
          quantity: +res.availableQuantity,
          pharmacyDiscount: +res.businessPriceDiscountPercentage,
          sellerDiscount: +res.sellerPriceDiscountPercentage,
          peoplePrice: +res.customerPrice,
        });
        this.setupPriceCalculation();
      },
    });
  }

  loadSuppliers() {
    const subscription = this.suppliersService.getAllSuppliers({ pageSize: 100, isActive: true }).subscribe({
      next: (res: AdminSuppliersResponseBody) => this.suppliers.set(res.list),
    });
    this.destroyRef.onDestroy(() => {
      subscription.unsubscribe();
    });
  }

  setupPriceCalculation() {
    const subscription = this.stockForm.valueChanges.subscribe(() => {
      const val = this.stockForm.getRawValue();
      const p = val.peoplePrice || 0;
      const sD = val.sellerDiscount ?? 0;
      const pD = val.pharmacyDiscount ?? 0;

      this.stockForm.patchValue(
        {
          sellerPrice: Number((p - (p * sD) / 100).toFixed(2)),
          pharmacyPrice: Number((p - (p * pD) / 100).toFixed(2)),
        },
        { emitEvent: false },
      );
    });

    this.destroyRef.onDestroy(() => {
      subscription.unsubscribe();
    });
  }

  onProductSearch(term: any) {
    const value = term?.target ? term.target.value : term;
    if (!value || value.trim().length === 0) {
      this.productsAutocomplete.set([]);
      this.showProductAutocomplete.set(false);
      this.stockForm.patchValue({ productId: null });
      return;
    }
    if (value.length >= 2) {
      const subscription = this.productService.autocompleteAdminProducts(value).subscribe((res) => {
        this.productsAutocomplete.set(res.list);
        this.showProductAutocomplete.set(true);
      });
      this.destroyRef.onDestroy(() => {
        subscription.unsubscribe();
      });
    }
  }

  selectProduct(prod: ProductAutocompleteItem) {
    this.stockForm.patchValue({
      productId: prod.id,
      productSearch: this.getTranslatedTitle(prod),
      peoplePrice: prod.peoplePrice ?? null,
      sellerDiscount: prod.sellerPriceDiscountPercentage ?? 0,
      pharmacyDiscount: prod.pharmacyPriceDiscountPercentage ?? 0,
    });
    this.showProductAutocomplete.set(false);
  }

  getTranslatedTitle(item: ProductAutocompleteItem | SupplierItem): string {
    if (this.locale.startsWith('ar')) return item.titleAr ?? item.titleEn ?? '';
    return item.titleEn ?? item.titleAr ?? '';
  }

  private getControlErrorMessage(name: string): string | null {
    const ctrl = this.stockForm.get(name);
    if (!ctrl || !ctrl.invalid || !ctrl.touched) return null;
    const ar = this.locale.startsWith('ar');
    const errors = ctrl.errors || {};

    switch (name) {
      case 'productId':
      case 'productSearch':
        if (errors['required'])
          return ar ? 'الصنف مطلوب. يرجى اختيار صنف.' : 'Product is required. Please select a product.';
        break;
      case 'supplierId':
        if (errors['required'])
          return ar ? 'المورد مطلوب. يرجى اختيار مورد.' : 'Supplier is required. Please select a supplier.';
        break;
      case 'quantity':
        if (errors['required'])
          return ar ? 'الكمية مطلوبة. يرجى إدخال قيمة الكمية.' : 'Quantity is required. Please enter a quantity value.';
        if (errors['min']) return ar ? 'الكمية يجب أن تكون أكبر من صفر.' : 'Quantity must be greater than 0.';
        if (errors['notInteger'])
          return ar
            ? 'الكمية يجب أن تكون رقماً صحيحاً. القيم العشرية غير مسموح بها.'
            : 'Quantity must be a whole number. Decimal values are not allowed.';
        break;
      case 'batchNumber':
        if (errors['required'])
          return ar
            ? 'رقم الدفعة مطلوب. يرجى إدخال رقم الدفعة.'
            : 'Batch Number is required. Please enter a batch number.';
        if (errors['maxlength'])
          return ar ? 'لا يمكن أن يتجاوز رقم الدفعة 50 حرفًا.' : 'Batch Number cannot exceed 50 characters.';
        if (errors['pattern'])
          return ar
            ? 'رقم الدفعة يمكن أن يحتوي على حروف وأرقام وشرطات فقط.'
            : 'Batch Number can contain letters, numbers, and hyphens only.';
        if (errors['duplicate'])
          return ar
            ? 'رقم الدفعة هذا مضاف بالفعل في القائمة المؤقتة.'
            : 'This batch number has already been added to the temporary list.';
        break;
      case 'mfgDate':
        if (errors['required'])
          return ar
            ? 'تاريخ الإنتاج مطلوب. يرجى اختيار تاريخ.'
            : 'Manufacturing Date is required. Please select a date.';
        if (errors['futureDate'] || errors['backendMsg'])
          return ar ? 'تاريخ الإنتاج لا يمكن أن يكون في المستقبل.' : 'Manufacturing Date cannot be in the future.';
        if (errors['afterExpDate'])
          return ar
            ? 'تاريخ الإنتاج يجب أن يكون قبل تاريخ الانتهاء.'
            : 'Manufacturing Date must be before Expiration Date.';
        break;
      case 'expDate':
        if (errors['required'])
          return ar ? 'تاريخ الانتهاء مطلوب. يرجى اختيار تاريخ.' : 'Expiration Date is required. Please select a date.';
        if (errors['notFuture'])
          return ar ? 'تاريخ الانتهاء يجب أن يكون تاريخاً مستقبلياً.' : 'Expiration Date must be a future date.';
        if (errors['beforeMfgDate'])
          return ar
            ? 'تاريخ الانتهاء يجب أن يكون بعد تاريخ الإنتاج.'
            : 'Expiration Date must be after Manufacturing Date.';
        break;
      case 'peoplePrice':
        if (errors['required']) return ar ? 'سعر الجمهور مطلوب' : 'People Price is required';
        if (errors['min'])
          return ar ? 'سعر الجمهور يجب أن يكون أكبر من صفر' : 'People Price must be greater than 0 if provided';
        if (errors['max'])
          return ar ? 'سعر الجمهور يجب ألا يتجاوز 1,000,000.00 ج.م' : 'People Price must not exceed 1,000,000.00 EGP';
        if (errors['pattern']) return ar ? 'يرجى إدخال سعر صحيح' : 'Please enter a valid price';
        break;
      case 'sellerDiscount':
        if (errors['required']) return ar ? 'نسبة خصم التاجر مطلوبة' : 'Seller Discount is required';
        if (errors['min']) return ar ? 'نسبة خصم التاجر لا يمكن أن يكون سالب' : 'Seller Discount cannot be negative';
        if (errors['max']) return ar ? 'نسبة خصم التاجر لا يمكن أن يتجاوز 100%' : 'Seller Discount cannot exceed 100%';
        break;
      case 'pharmacyDiscount':
        if (errors['required']) return ar ? 'نسبة خصم الصيدلية مطلوبة' : 'Pharmacy Discount is required';
        if (errors['min'])
          return ar ? 'نسبة خصم الصيدلية لا يمكن أن يكون سالب' : 'Pharmacy Discount cannot be negative';
        if (errors['max'])
          return ar ? 'نسبة خصم الصيدلية لا يمكن أن يتجاوز 100%' : 'Pharmacy Discount cannot exceed 100%';
        break;
    }
    return null;
  }

  private readonly formFieldOrder = [
    'productId',
    'productSearch',
    'supplierId',
    'quantity',
    'batchNumber',
    'mfgDate',
    'expDate',
    'peoplePrice',
    'sellerDiscount',
    'pharmacyDiscount',
  ];

  getFormValidationError(): string {
    for (const name of this.formFieldOrder) {
      const msg = this.getControlErrorMessage(name);
      if (msg) return msg;
    }

    const val = this.stockForm.getRawValue();
    if (
      this.stockForm.touched &&
      val.sellerDiscount != null &&
      val.pharmacyDiscount != null &&
      val.sellerDiscount < val.pharmacyDiscount
    ) {
      return this.locale.startsWith('ar')
        ? 'يجب الحفاظ على تسلسل الأسعار: سعر التاجر ≤ سعر الصيدلية ≤ سعر الجمهور'
        : 'Price hierarchy must be maintained: Seller Price ≤ Pharmacy Price ≤ People Price';
    }

    return '';
  }

  private clearCustomErrors() {
    ['mfgDate', 'expDate', 'batchNumber'].forEach((name) => {
      const ctrl = this.stockForm.get(name);
      if (!ctrl?.errors) return;
      const { futureDate, notFuture, afterExpDate, beforeMfgDate, duplicate, ...rest } = ctrl.errors;
      ctrl.setErrors(Object.keys(rest).length ? rest : null);
    });
  }

  private validateBusinessRules(): boolean {
    this.clearCustomErrors();
    let valid = true;

    const val = this.stockForm.getRawValue();
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const mfgCtrl = this.stockForm.get('mfgDate');
    const expCtrl = this.stockForm.get('expDate');

    if (val.mfgDate) {
      const mfg = new Date(val.mfgDate);
      if (mfg > today) {
        mfgCtrl?.setErrors({ ...mfgCtrl.errors, futureDate: true });
        valid = false;
      }
    }

    if (val.expDate) {
      const exp = new Date(val.expDate);
      if (exp <= today) {
        expCtrl?.setErrors({ ...expCtrl.errors, notFuture: true });
        valid = false;
      }
    }

    if (val.mfgDate && val.expDate) {
      const mfg = new Date(val.mfgDate);
      const exp = new Date(val.expDate);
      if (mfg >= exp) {
        mfgCtrl?.setErrors({ ...mfgCtrl.errors, afterExpDate: true });
        expCtrl?.setErrors({ ...expCtrl.errors, beforeMfgDate: true });
        valid = false;
      }
    }

    if (val.sellerDiscount != null && val.pharmacyDiscount != null && val.sellerDiscount < val.pharmacyDiscount) {
      valid = false;
    }

    const batch = (val.batchNumber || '').trim().toLowerCase();
    if (batch && this.temporaryStockList().some((item) => item.batchNumber.trim().toLowerCase() === batch)) {
      const batchCtrl = this.stockForm.get('batchNumber');
      batchCtrl?.setErrors({ ...batchCtrl.errors, duplicate: true });
      valid = false;
    }

    return valid;
  }

  addToList() {
    this.stockForm.markAllAsTouched();
    this.stockForm.updateValueAndValidity();

    const businessRulesValid = this.validateBusinessRules();

    if (!this.stockForm.valid || !businessRulesValid) {
      return;
    }

    const val = this.stockForm.getRawValue();

    const newItem: StockIncomingItem = {
      productId: val.productId!,
      supplierId: Number(val.supplierId),
      quantity: val.quantity!,
      peoplePrice: val.peoplePrice!,
      sellerPriceDiscountPercentage: val.sellerDiscount!,
      pharmacyPriceDiscountPercentage: val.pharmacyDiscount!,
      batchNumber: val.batchNumber!,
      mfgDate: val.mfgDate!,
      expDate: val.expDate!,
      productTitle: val.productSearch!,
      id: Date.now(),
    };

    this.temporaryStockList.update((list) => [...list, newItem]);
    this.stockForm.reset({ supplierId: val.supplierId });
  }

  editItem(index: number) {
    const item = this.temporaryStockList()[index];
    this.stockForm.patchValue({
      productId: item.productId,
      productSearch: item.productTitle,
      supplierId: item.supplierId,
      quantity: item.quantity,
      peoplePrice: item.peoplePrice,
      sellerDiscount: item.sellerPriceDiscountPercentage,
      pharmacyDiscount: item.pharmacyPriceDiscountPercentage,
      batchNumber: item.batchNumber,
      mfgDate: item.mfgDate,
      expDate: item.expDate,
    });
    this.removeItem(item.id!);
  }

  removeItem(id: number) {
    this.temporaryStockList.update((list) => list.filter((item) => item.id !== id));
  }

  resetAll() {
    if (confirm('Are you sure?')) {
      this.temporaryStockList.set([]);
      this.stockForm.reset();
      this.pageError.set('');
      this.showSuccess.set(false);
    }
  }

  submitAll() {
    if (this.temporaryStockList().length === 0) {
      return;
    }
    this.pageError.set('');
    this.showSuccess.set(false);

    const finalData = {
      list: this.temporaryStockList().map(({ id, productTitle, ...rest }) => rest),
    };

    const subscription = this.stockService.addBulkIncomingStock(finalData).subscribe({
      next: () => {
        this.temporaryStockList.set([]);
        this.showSuccess.set(true);
        setTimeout(() => {
          this.showSuccess.set(false);
          this.router.navigate(['/admin/stock']);
        }, 2000);
      },
      error: (err) => {
        const backendError = err.error;
        this.pageError.set(
          (this.locale.startsWith('ar') ? backendError?.messageAr : backendError?.messageEn) ??
            backendError?.message ??
            '',
        );
        this.stockForm.setErrors({ backend: backendError });
        if (backendError?.code === 'STOCK-0011') {
          this.stockForm.get('mfgDate')?.setErrors({ backendMsg: true });
        }
      },
    });

    this.destroyRef.onDestroy(() => {
      subscription.unsubscribe();
    });
  }
}
